import React, { useMemo, useRef, useState, useContext, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { AtSign, Send, Paperclip, Mic } from 'lucide-react';
import { useChatStore } from '../../stores/useChatStore.js';
import { beesClient } from '../../api/beesClient.js';
import toast from 'react-hot-toast';
import { HiveCoreContext } from '../../services/hivecore-scene-stream/HiveCoreProvider.js';
import { parseChatCommand, applyStudioUICommand } from '../../studio/panels';

type DictationState = 'idle' | 'connecting' | 'recording' | 'processing';

interface DictationMessage {
    type: string;
    sessionId?: string;
    traceId?: string;
    text?: string;
    confidence?: number;
    durationMs?: number;
    error?: string;
    code?: string;
    timestamp?: number;
}

const ChatInputBar = () => {
    const [message, setMessage] = useState('');
    const [attachments, setAttachments] = useState<File[]>([]);
    const [dictationState, setDictationState] = useState<DictationState>('idle');
    const { sendMessage, isLoading, addMessage, sessionId } = useChatStore();
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const voiceStreamRef = useRef<MediaStream | null>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const dictationWsRef = useRef<WebSocket | null>(null);
    const dictationSessionIdRef = useRef<string | null>(null);

    const hiveCoreContext = useContext(HiveCoreContext);
    const connected = hiveCoreContext?.connected ?? true;
    const connecting = hiveCoreContext ? !hiveCoreContext.connected : false;

    const voiceEnabled = useMemo(() => {
        // Default to enabled unless explicitly disabled (matches common repo gating pattern)
        return ((import.meta as any).env?.VITE_ENABLE_VOICE as string | undefined) !== 'false';
    }, []);

    // Cleanup WebSocket on unmount
    useEffect(() => {
        return () => {
            if (dictationWsRef.current) {
                dictationWsRef.current.close();
                dictationWsRef.current = null;
            }
        };
    }, []);

    const insertTextAtCursorRef = useRef<(text: string) => void>(() => {});

    const insertTextAtCursor = useCallback(
        (text: string) => {
            const el = textareaRef.current;
            if (!el) {
                setMessage(prev => prev + text);
                return;
            }

            const start = el.selectionStart ?? el.value.length;
            const end = el.selectionEnd ?? el.value.length;
            const nextValue = message.slice(0, start) + text + message.slice(end);

            setMessage(nextValue);

            // Keep focus + move caret after inserted text
            requestAnimationFrame(() => {
                el.focus();
                const nextPos = start + text.length;
                el.setSelectionRange(nextPos, nextPos);
            });
        },
        [message]
    );

    // Keep ref updated with latest insertTextAtCursor
    useEffect(() => {
        insertTextAtCursorRef.current = insertTextAtCursor;
    }, [insertTextAtCursor]);

    const handleMentionClick = () => {
        if (!connected) {
            toast.error('Not connected.');
            return;
        }
        insertTextAtCursor('@');
    };

    const handleAttachClick = () => {
        if (!connected) {
            toast.error('Not connected.');
            return;
        }
        fileInputRef.current?.click();
    };

    const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files ?? []);
        if (files.length === 0) return;

        setAttachments(prev => {
            // Avoid duplicates by (name,size,lastModified)
            const key = (f: File) => `${f.name}:${f.size}:${f.lastModified}`;
            const existing = new Set(prev.map(key));
            const next = [...prev];
            for (const f of files) {
                if (!existing.has(key(f))) next.push(f);
            }
            return next;
        });

        // Allow selecting the same file again later
        e.target.value = '';
    };

    const handleRemoveAttachment = (idx: number) => {
        setAttachments(prev => prev.filter((_, i) => i !== idx));
    };

    const stopVoiceStream = () => {
        if (voiceStreamRef.current) {
            for (const track of voiceStreamRef.current.getTracks()) {
                track.stop();
            }
            voiceStreamRef.current = null;
        }
    };

    const cleanupDictation = useCallback(() => {
        // Stop MediaRecorder if active
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
        }
        mediaRecorderRef.current = null;

        // Clear audio buffers
        audioChunksRef.current = [];

        // Stop all MediaStream tracks
        stopVoiceStream();

        // Close WebSocket connection
        if (dictationWsRef.current) {
            if (dictationWsRef.current.readyState === WebSocket.OPEN) {
                dictationWsRef.current.close();
            }
            dictationWsRef.current = null;
        }

        // Clear session reference
        dictationSessionIdRef.current = null;
    }, []);

    const getTranscriptionWsUrl = useCallback(() => {
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const host = window.location.host;
        const userId = sessionId || `user_${Date.now()}`;
        return `${protocol}//${host}/transcription?userId=${encodeURIComponent(userId)}`;
    }, [sessionId]);

    const handleDictationMessage = useCallback(
        (event: MessageEvent) => {
            try {
                const msg: DictationMessage = JSON.parse(event.data);
                console.log('[ChatInputBar] Dictation WS message:', msg.type);

                switch (msg.type) {
                    case 'connected':
                        console.log('[ChatInputBar] Connected to dictation service');
                        break;

                    case 'dictation_started':
                        dictationSessionIdRef.current = msg.sessionId || null;
                        setDictationState('recording');
                        toast.success('Recording...', { id: 'dictation-status', duration: 60000 });
                        break;

                    case 'final_transcript':
                        if (msg.text) {
                            insertTextAtCursorRef.current(msg.text);
                            toast.success(
                                `Transcribed (${msg.durationMs ? Math.round(msg.durationMs / 1000) : 0}s)`,
                                {
                                    id: 'dictation-status',
                                    duration: 2000,
                                }
                            );
                        } else {
                            toast('No speech detected', { id: 'dictation-status', duration: 2000 });
                        }
                        setDictationState('idle');
                        cleanupDictation();
                        break;

                    case 'dictation_cancelled':
                        toast('Recording cancelled', { id: 'dictation-status', duration: 2000 });
                        setDictationState('idle');
                        cleanupDictation();
                        break;

                    case 'dictation_error':
                        console.error('[ChatInputBar] Dictation error:', msg.error, msg.code);
                        toast.error(msg.error || 'Transcription failed', {
                            id: 'dictation-status',
                        });
                        setDictationState('idle');
                        cleanupDictation();
                        break;

                    case 'pong':
                        break;

                    default:
                        console.log('[ChatInputBar] Unknown dictation message type:', msg.type);
                }
            } catch (err) {
                console.error('[ChatInputBar] Error parsing dictation message:', err);
            }
        },
        [cleanupDictation]
    );

    const startDictation = useCallback(async () => {
        setDictationState('connecting');
        toast.loading('Connecting...', { id: 'dictation-status' });

        try {
            // Get microphone access
            if (!navigator.mediaDevices?.getUserMedia) {
                throw new Error('Microphone not supported in this environment.');
            }

            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            voiceStreamRef.current = stream;

            // Connect to WebSocket
            const wsUrl = getTranscriptionWsUrl();
            const ws = new WebSocket(wsUrl);
            dictationWsRef.current = ws;

            ws.onopen = () => {
                console.log('[ChatInputBar] Dictation WebSocket connected');

                // Start dictation session
                const traceId = `trace_${Date.now()}`;
                ws.send(
                    JSON.stringify({
                        type: 'start_dictation',
                        traceId,
                        mimeType: 'audio/webm;codecs=opus',
                    })
                );

                // Set up MediaRecorder
                const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
                    ? 'audio/webm;codecs=opus'
                    : 'audio/webm';

                const mediaRecorder = new MediaRecorder(stream, { mimeType });
                mediaRecorderRef.current = mediaRecorder;
                audioChunksRef.current = [];

                mediaRecorder.ondataavailable = event => {
                    if (event.data.size > 0) {
                        audioChunksRef.current.push(event.data);
                    }
                };

                mediaRecorder.onstop = async () => {
                    console.log('[ChatInputBar] MediaRecorder stopped, sending audio...');

                    if (audioChunksRef.current.length === 0) {
                        ws.send(JSON.stringify({ type: 'cancel_dictation' }));
                        return;
                    }

                    // Combine all chunks and send
                    const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
                    const arrayBuffer = await audioBlob.arrayBuffer();
                    const base64Audio = btoa(
                        new Uint8Array(arrayBuffer).reduce(
                            (data, byte) => data + String.fromCharCode(byte),
                            ''
                        )
                    );

                    // Send audio chunk
                    ws.send(
                        JSON.stringify({
                            type: 'audio_chunk',
                            audioData: base64Audio,
                        })
                    );

                    // Stop dictation to trigger transcription
                    ws.send(JSON.stringify({ type: 'stop_dictation' }));
                    setDictationState('processing');
                    toast.loading('Transcribing...', { id: 'dictation-status' });
                };

                // Start recording with timeslice for chunked data
                mediaRecorder.start(1000);
            };

            ws.onmessage = handleDictationMessage;

            ws.onerror = error => {
                console.error('[ChatInputBar] Dictation WebSocket error:', error);
                toast.error('Connection error', { id: 'dictation-status' });
                setDictationState('idle');
                cleanupDictation();
            };

            ws.onclose = () => {
                console.log('[ChatInputBar] Dictation WebSocket closed');
                if (dictationState === 'recording' || dictationState === 'connecting') {
                    setDictationState('idle');
                    cleanupDictation();
                }
            };
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'Microphone permission denied.';
            toast.error(msg, { id: 'dictation-status' });
            setDictationState('idle');
            cleanupDictation();
        }
    }, [getTranscriptionWsUrl, handleDictationMessage, cleanupDictation, dictationState]);

    const stopDictation = useCallback(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.stop();
        }
    }, []);

    const cancelDictation = useCallback(() => {
        if (dictationWsRef.current && dictationWsRef.current.readyState === WebSocket.OPEN) {
            dictationWsRef.current.send(JSON.stringify({ type: 'cancel_dictation' }));
        }
        setDictationState('idle');
        cleanupDictation();
        toast.dismiss('dictation-status');
    }, [cleanupDictation]);

    const handleVoiceClick = async () => {
        if (!connected) {
            toast.error('Not connected.');
            return;
        }
        if (!voiceEnabled) {
            toast('Voice input is disabled in this build.', { id: 'voice-disabled' });
            return;
        }

        // Toggle behavior based on current state
        switch (dictationState) {
            case 'idle':
                await startDictation();
                break;
            case 'connecting':
                cancelDictation();
                break;
            case 'recording':
                stopDictation();
                break;
            case 'processing':
                // Can't cancel while processing, just wait
                toast('Processing audio, please wait...', { id: 'dictation-wait' });
                break;
        }
    };

    // Derived state for backward compatibility
    const isVoiceRecording =
        dictationState === 'recording' ||
        dictationState === 'connecting' ||
        dictationState === 'processing';

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (message.trim() && !isLoading && connected) {
            // B3: Check for UI commands first (/open, /drawer)
            const uiCommand = parseChatCommand(message);
            if (uiCommand) {
                console.log('[ChatInputBar] Executing UI command:', uiCommand);
                applyStudioUICommand(uiCommand);
                addMessage({
                    sender: 'system',
                    content: `Executed: ${message.trim()}`,
                    type: 'text',
                    messageType: 'chat',
                    buildCredits: 0,
                });
                setMessage('');
                return;
            }

            const workflowTriggers = [
                '/deploy',
                '/build',
                '/test',
                '/run',
                '/start',
                '/execute',
                '/install',
                '/setup',
            ];
            const needsEnvCheck = workflowTriggers.some(
                trigger =>
                    message.toLowerCase().includes(trigger) ||
                    message.toLowerCase().includes('workflow') ||
                    message.toLowerCase().includes('automation')
            );

            if (needsEnvCheck) {
                try {
                    const readyResponse = await fetch('/api/monitoring/ready');
                    const readyData = await readyResponse.json();

                    // Support both response formats: { ready: true } and { status: "ready" }
                    const isReady = readyData.ready === true || readyData.status === 'ready';
                    if (!isReady) {
                        const blockers = readyData.blockers || [];
                        if (blockers.length > 0) {
                            const blockerMessages = blockers
                                .slice(0, 3)
                                .map(
                                    (b: { id: string; message: string; remediation: string }) =>
                                        `[${b.id}] ${b.message}`
                                )
                                .join('\n');
                            const remediationSteps = blockers
                                .slice(0, 3)
                                .map((b: { remediation: string }) => b.remediation)
                                .filter((r: string) => r)
                                .join('\n');

                            toast.error(
                                <div className="text-left">
                                    <div className="font-bold mb-1">
                                        Environment validation failed:
                                    </div>
                                    <div className="text-sm whitespace-pre-wrap">
                                        {blockerMessages}
                                    </div>
                                    {remediationSteps && (
                                        <div className="mt-2 text-xs text-slate-300">
                                            <div className="font-medium">Fix steps:</div>
                                            <div className="whitespace-pre-wrap">
                                                {remediationSteps}
                                            </div>
                                        </div>
                                    )}
                                </div>,
                                {
                                    duration: 8000,
                                    position: 'top-center',
                                }
                            );
                        } else {
                            toast.error(
                                'Environment validation required before executing workflows. Please check your environment setup.',
                                {
                                    duration: 5000,
                                    position: 'top-center',
                                }
                            );
                        }
                        return;
                    }
                } catch (error) {
                    console.warn('Pre-workflow environment check failed:', error);
                    toast.error('Unable to validate environment. Please check your setup.', {
                        duration: 5000,
                        position: 'top-center',
                    });
                    return;
                }
            }

            const trimmed = message.trim();
            const attachmentMeta =
                attachments.length > 0
                    ? `\n\nAttachments:\n${attachments
                          .map(f => `- ${f.name} (${Math.ceil(f.size / 1024)} KB)`)
                          .join('\n')}`
                    : '';

            await sendMessage(trimmed + attachmentMeta);
            setMessage('');
            setAttachments([]);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e as React.FormEvent);
        }
    };

    // Ledger Mode UI Contract: Glow = exception, Flat = default
    // - All states: flat neutral border, no glow
    // - Connection state: status strip only, NOT input border
    // - Focus: subtle ring (handled by focus-within)
    // - Alerts: color reserved for HITL or error only
    const glassBarClasses = [
        'w-full flex flex-col px-5 py-4 rounded-2xl',
        'bg-slate-950/70 backdrop-blur-xl',
        'border border-slate-600/60',
        'transition-all duration-150 ease-out',
    ].join(' ');

    // Ledger Mode UI Contract: Send button - flat default, subtle hover
    // No persistent glow - glow only on hover/active as affordance
    const sendButtonClasses = [
        'h-9 w-9 rounded-full flex items-center justify-center flex-shrink-0',
        'bg-gradient-to-br from-slate-600 to-slate-500',
        connected ? 'opacity-100 hover:from-emerald-500 hover:to-cyan-500' : 'opacity-35',
        'hover:shadow-[0_0_8px_rgba(34,197,94,0.25)]',
        'transition-all duration-150 ease-out',
        'disabled:opacity-25 disabled:cursor-not-allowed disabled:hover:shadow-none',
    ].join(' ');

    return (
        <div className="flex flex-col h-full">
            {/* Input Area - outer wrapper is layout only, no border/bg */}
            <div className="flex-1 flex items-end px-6 pb-6 pt-3 w-full">
                {/* Glass bar - this is the ONLY visual element */}
                <div className={glassBarClasses}>
                    {/* Top row: textarea + send button */}
                    <div className="flex items-start gap-3 w-full">
                        <textarea
                            ref={textareaRef}
                            value={message}
                            onChange={e => setMessage(e.target.value)}
                            onKeyDown={handleKeyPress}
                            placeholder={
                                connected
                                    ? 'Direct Soma Noesis...'
                                    : connecting
                                      ? 'Connecting to HiveCore...'
                                      : 'Reconnecting...'
                            }
                            className="flex-1 bg-transparent resize-none min-h-[88px] max-h-40 py-2 text-[15px] leading-6 text-slate-50 placeholder:text-slate-500 focus:outline-none overflow-y-auto scrollbar-none [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                            rows={3}
                            disabled={isLoading || !connected}
                        />
                        {/* Send Button with glow */}
                        <motion.button
                            onClick={handleSubmit}
                            disabled={!message.trim() || isLoading || !connected}
                            className={sendButtonClasses}
                            whileHover={
                                message.trim() && !isLoading && connected ? { scale: 1.05 } : {}
                            }
                            whileTap={
                                message.trim() && !isLoading && connected ? { scale: 0.95 } : {}
                            }
                            title="Send message"
                            data-testid="send-button"
                        >
                            {isLoading ? (
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <Send className="w-5 h-5 text-slate-900" />
                            )}
                        </motion.button>
                    </div>

                    {/* Attachments row */}
                    {attachments.length > 0 && (
                        <div className="pt-2">
                            <div className="flex flex-wrap gap-2">
                                {attachments.map((file, idx) => (
                                    <div
                                        key={`${file.name}:${file.size}:${file.lastModified}`}
                                        className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-slate-900/60 border border-slate-700/50 text-xs text-slate-200"
                                        data-testid="attachment-chip"
                                        title={`${file.name} (${file.size} bytes)`}
                                    >
                                        <span className="max-w-[220px] truncate">{file.name}</span>
                                        <button
                                            type="button"
                                            className="text-slate-400 hover:text-white"
                                            onClick={() => handleRemoveAttachment(idx)}
                                            aria-label={`Remove attachment ${file.name}`}
                                            data-testid={`remove-attachment-${idx}`}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Bottom row: Action icons inside the glass bar */}
                    <div className="flex items-center justify-between pt-2 border-t border-slate-700/30 mt-2">
                        <div className="flex items-center space-x-1">
                            <motion.button
                                type="button"
                                onClick={handleMentionClick}
                                className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/60 transition-all duration-300"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                title="Mention"
                                data-testid="mention-button"
                            >
                                <AtSign className="w-4 h-4" />
                            </motion.button>
                            <motion.button
                                type="button"
                                onClick={handleAttachClick}
                                className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/60 transition-all duration-300"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                title="Attach File"
                                data-testid="attach-button"
                            >
                                <Paperclip className="w-4 h-4" />
                            </motion.button>
                            <motion.button
                                type="button"
                                onClick={handleVoiceClick}
                                aria-pressed={isVoiceRecording}
                                className={[
                                    'p-2 rounded-lg transition-all duration-300',
                                    isVoiceRecording
                                        ? 'text-red-300 bg-red-500/15 hover:bg-red-500/20'
                                        : 'text-slate-400 hover:text-white hover:bg-slate-700/60',
                                ].join(' ')}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                title={isVoiceRecording ? 'Stop recording' : 'Voice Input'}
                                data-testid="mic-button"
                            >
                                <Mic className="w-4 h-4" />
                            </motion.button>
                        </div>
                    </div>

                    {/* Hidden file input for attachments */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        onChange={handleFilesSelected}
                        multiple
                        aria-hidden="true"
                        tabIndex={-1}
                        data-testid="file-input"
                    />
                </div>
            </div>
        </div>
    );
};

export default ChatInputBar;
